/**
 * 列出可用模板工具
 * 
 * 设计理念：提供模板列表信息给 AI，AI 自己决定使用哪个
 * 
 * @module tools/listTemplates
 */

import { 
    listAllTemplates, 
    listTemplatesByType, 
    listTemplatesByFramework,
    searchTemplates 
} from '../core/templates/discovery';
import { TemplateType } from '../core/templates/types';
import { ConsoleLogger } from '../core/types';

/**
 * 列出可用模板
 * 
 * @example
 * // 列出所有模板
 * list_templates({})
 * 
 * @example
 * // 按类型筛选
 * list_templates({ type: 'api-layer' })
 * 
 * @example
 * // 按框架筛选
 * list_templates({ framework: 'vue' })
 * 
 * @example
 * // 搜索模板
 * list_templates({ search: 'axios' })
 */
export async function listTemplates(args: {
    /** 按类型筛选 */
    type?: TemplateType;
    /** 按框架筛选 */
    framework?: string;
    /** 搜索关键词 */
    search?: string;
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();
    
    try {
        let templates;
        
        if (args.search) {
            templates = searchTemplates(args.search);
        } else if (args.type) {
            templates = listTemplatesByType(args.type);
        } else if (args.framework) {
            templates = listTemplatesByFramework(args.framework);
        } else {
            templates = listAllTemplates();
        }
        
        // 按类型分组展示
        const grouped: Record<string, typeof templates> = {};
        for (const t of templates) {
            if (!grouped[t.type]) grouped[t.type] = [];
            grouped[t.type].push(t);
        }
        
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    success: true,
                    total: templates.length,
                    templates,
                    byType: grouped,
                    usage: {
                        getDetails: 'get_template({ id: "模板ID" })',
                        getWithFiles: 'get_template({ id: "模板ID", includeFiles: true })',
                        note: 'AI 可直接读取模板文件并复制到目标项目'
                    }
                }, null, 2)
            }]
        };
    } catch (error) {
        logger.error(`列出模板失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}
